import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { initCapacitorBridge, isNative } from '../lib/capacitor';

/**
 * Mounts the Capacitor bridge at the BrowserRouter scope so it can call
 * `useNavigate` for deep-link handling. Renders a small offline pill at
 * the top of the viewport when the network drops — invisible on a normal
 * web session because the bridge no-ops there.
 *
 * Sits as a sibling of <Routes>, not a child, so React Router doesn't
 * re-mount it on every route change.
 */
export function CapacitorBridge({ onResume }: { onResume?: () => void }) {
  const navigate = useNavigate();
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    let cleanup: (() => void) | null = null;
    let cancelled = false;
    void initCapacitorBridge({
      navigate: (path) => navigate(path),
      onResume,
      onNetworkChange: (status) => setOffline(status === 'offline'),
    }).then((teardown) => {
      if (cancelled) {
        try {
          teardown();
        } catch {
          // ignore
        }
      } else {
        cleanup = teardown;
      }
    });
    return () => {
      cancelled = true;
      if (cleanup) cleanup();
    };
  }, [navigate, onResume]);

  if (!isNative() || !offline) return null;

  return (
    <div className="offline-banner" role="status" aria-live="polite">
      You're offline. Messages will send when the connection is back.
    </div>
  );
}
